import type {
  GameWithTags,
  QuizSituation,
} from "../../../services/gameQuiz/gameQuiz.types";

const LABEL_TEXT_LENGTH = 40;

export const createSituation = (): QuizSituation => ({
  id: `s${Date.now()}${Math.random().toString(36).substring(2, 9)}`,
  text: "",
  image_url: null,
});

const snippet = (text: string): string => {
  const plain = text.trim().replace(/\s+/g, " ");
  return plain.length > LABEL_TEXT_LENGTH
    ? `${plain.slice(0, LABEL_TEXT_LENGTH - 1)}…`
    : plain;
};

export function situationName(situations: QuizSituation[], id: string): string {
  const index = situations.findIndex((situation) => situation.id === id);
  return index === -1 ? "Situación eliminada" : `Situación ${index + 1}`;
}

export function situationLabel(
  situations: QuizSituation[],
  id: string,
): string {
  const name = situationName(situations, id);
  const text = snippet(
    situations.find((situation) => situation.id === id)?.text ?? "",
  );
  return text ? `${name} · ${text}` : name;
}

export function findSituationError(
  situations: QuizSituation[],
  games: GameWithTags[],
): string | null {
  const emptyIndex = situations.findIndex(
    (situation) => !situation.text.trim(),
  );
  if (emptyIndex !== -1) {
    return `La situación ${emptyIndex + 1} necesita un texto.`;
  }

  const positions = new Map(
    situations.map((situation, index) => [situation.id, index + 1]),
  );
  const used = new Set<string>();
  let previous: string | null = null;
  for (let index = 0; index < games.length; index += 1) {
    const id = games[index].situation_id;
    if (!id) {
      previous = null;
      continue;
    }
    if (!positions.has(id)) {
      return `La pregunta #${index + 1} tiene una situación que ya no existe.`;
    }
    if (used.has(id) && previous !== id) {
      return `Las preguntas de la situación ${positions.get(id)} deben ir seguidas: la pregunta #${index + 1} quedó separada de las anteriores.`;
    }
    used.add(id);
    previous = id;
  }

  const unused = situations.findIndex((situation) => !used.has(situation.id));
  return unused === -1
    ? null
    : `La situación ${unused + 1} no tiene preguntas: asígnala a una pregunta o elimínala.`;
}

export function removeSituation(
  situations: QuizSituation[],
  games: GameWithTags[],
  id: string,
): { situations: QuizSituation[]; games: GameWithTags[] } {
  return {
    situations: situations.filter((situation) => situation.id !== id),
    games: games.map((game) => {
      if (game.situation_id !== id) return game;
      const { situation_id: _removed, ...rest } = game;
      return rest as GameWithTags;
    }),
  };
}
